<template>
  <div class="actions-dropdown-container" ref="containerRef">

    <button 
      class="button-light actions-button" 
      :class="{ 'actions-button--highlighted': selectedCount > 0 }"
      @click.stop="toggleDropdown()"
    >
      <span v-if="label">{{ label }}</span>
      <SVGIcon
        :iconName="label ? 'expand_more' : 'more_vert'"
        :width="label ? '24px' : '72px'"
        :height="label ? '24px' : '72px'"
        :style="label ? 'margin-left: 6px;' : ''"
      />
      <span v-if="selectedCount > 0" class="selection-badge">{{ selectedCount }}</span>
    </button>
    <div v-show="showDropdown" class="actions-dropdown">
      <ActionItem
        v-for="action in actions"
        :key="action.eventKey"
        :icon="action.icon"
        :label="action.label"
        :eventKey="action.eventKey"
        :isAlert="action.isAlert"
        :isWarn="action.isWarn"
        :disabled="action.disabled"
        :tooltip="action.tooltip"
        @click="handleActionClick"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, onMounted, onUnmounted } from 'vue';
import SVGIcon from '@/components/SVGIcon.vue';
import ActionItem from '@/components/ActionItem.vue';

const props = defineProps({
  /**
   * Array of action objects
   * Each object should have: { icon?, label, eventKey, isAlert?, isWarn?, disabled? }
   */
  actions: {
    type: Array,
    required: true,
    validator: (actions) => {
      return actions.every(action => 
        action.label && action.eventKey
      );
    },
  },
  /**
   * Button label text. If not provided, shows three dots icon
   */
  label: {
    type: String,
    default: '',
  },
  /**
   * Button style: 'chevron' (expand_more) or 'dots' (more_vert)
   */
  buttonStyle: {
    type: String,
    default: 'chevron',
    validator: (value) => ['chevron', 'dots'].includes(value),
  },
  /**
   * Show selection count in the button
   */
  showCount: {
    type: Boolean,
    default: false,
  },
  /**
   * Current selection count to display
   */
  selectedCount: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['action-clicked']);

const showDropdown = ref(false);
const containerRef = ref(null);

const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value;
};

const closeDropdown = () => {
  showDropdown.value = false;
};

const handleActionClick = (eventKey) => {
  emit('action-clicked', eventKey);
  closeDropdown();
};

const handleClickOutside = (event) => {
  if (containerRef.value && !containerRef.value.contains(event.target)) {
    closeDropdown();
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style lang="scss" scoped>
@import '@/assets/css/themes/auzone/buttons.scss';

.actions-dropdown-container {
  position: relative;
  display: flex;
  align-items: center;
}

.actions-button {
  &:has(span:not(.selection-badge)) {
    // Labeled button (has text label)
    &:hover {
      background: rgba(147, 162, 222, 0.15);
      border-radius: 4px;
    }
  }

  &:not(:has(span:not(.selection-badge))) {
    // Dots-only button (no text label)
    border-radius: 50%;
    width: 32px;
    height: 32px;
    padding: 4px;

    &:hover {
      background: #93a2de;
    }
  }

  &--highlighted {
    &:has(span:not(.selection-badge)) {
      // Keep rectangular for labeled buttons
      background: rgba(147, 162, 222, 0.15);
    }

    &:not(:has(span:not(.selection-badge))) {
      // Circular highlight for dots-only buttons
      background: #93a2de;
      border-radius: 50%;
      width: 32px;
      height: 32px;
      padding: 4px;
    }
  }
}

.selection-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background: #ef4444;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
}

.actions-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  z-index: 1000;
  min-width: 225px;
  margin-top: 6px;
  padding: 4px;
}

</style>
